/*
 * Collects the search tracking fields that can only be sourced in the browser
 * (the Lytics subscriber flag) and fires a fire-and-forget POST beacon with the
 * search query to a dedicated tracking endpoint. The backend generates the
 * timestamp and site_id. Previous-search chaining and same-query suppression
 * are managed via localStorage so they survive across page loads without a
 * Django session.
 *
 * Also defines listeners for triggering GTM events on search
 */

import { v4 as uuidv4 } from 'uuid';
import {
  pushSearchExecutedEvent,
  handleSearchResultLinkClick,
} from './searchGtmEvents';
import ourLocalStorage from '../storage/localStorage';

const SEARCH_TRACKING_STORAGE_KEY = 'dive_search_tracking';

function getPreviousSearch() {
  const previous = ourLocalStorage.getItem(SEARCH_TRACKING_STORAGE_KEY);
  return previous && typeof previous === 'object' ? previous : {};
}

function saveCurrentSearch(entry) {
  ourLocalStorage.setItem(SEARCH_TRACKING_STORAGE_KEY, JSON.stringify(entry));
}

function getSubscriberStatus() {
  // Checks if the user is a subscriber by looking for "any_<site>_dive_subs" in their
  // Lytics audience segments. Education Dive checks both Higher Ed and K-12 segments.
  // Returns false if Lytics is unavailable or the segment is not found.
  try {
    const siteName = (window.siteName || '').toLowerCase();
    if (!siteName) return false;

    const { jstag } = window;
    const segments = jstag?.config?.user?.segments
      || jstag?.loader?.entity?.data?.user?.segments
      || [];

    if (!segments.length) return false;

    const subscriberAudiences = siteName === 'education dive'
      ? ['any_higher_ed_dive_subs', 'any_k12_dive_subs']
      : [`any_${siteName.replace(/ /g, '_').replace('-', '')}_subs`];

    return subscriberAudiences.some((audience) => segments.includes(audience));
  } catch (err) {
    return false;
  }
}

function sendTrackingBeacon(url, payload) {
  // Sends a fire-and-forget POST with the tracking payload. Uses sendBeacon when
  // available to ensure the request survives page navigation. Falls back to fetch
  // with keepalive for browsers that don't support sendBeacon.
  const body = JSON.stringify(payload);
  try {
    if (navigator?.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) return;
    }
    fetch(url, {
      method: 'POST',
      body,
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      credentials: 'same-origin',
    }).catch(() => {});
  } catch (err) {
    // Silently fail
  }
}

const trackedForms = new WeakSet();

function trackSearchSubmit(form) {
  const $form = $(form);
  const rawQuery = ($form.find('input[name="q"]').val() || '').trim();
  if (!rawQuery) return;

  // Prevent duplicate GTM events from button-mashing the same form.
  if (trackedForms.has(form)) {
    return;
  }
  trackedForms.add(form);

  // Dedicated endpoint is provided by data-search-tracking-url on the form.
  const trackingUrl = form.dataset.searchTrackingUrl || form.action || window.location.href;

  // Push GA4 event for search execution
  const isOnSemanticSearchPage = form.classList.contains('semantic-search-form');
  pushSearchExecutedEvent({
    eventName: 'search_executed',
    searchTerm: rawQuery,
    searchOrigin: isOnSemanticSearchPage ? 'search_page' : 'nav_bar',
  });

  const previous = getPreviousSearch();
  const previousQuery = (previous.query || '').trim().toLowerCase();
  if (previousQuery && previousQuery === rawQuery.toLowerCase()) {
    // Same query resubmission: ignore tracking beacon; keep storage unchanged.
    return;
  }

  const uuid = uuidv4();
  sendTrackingBeacon(trackingUrl, {
    uuid,
    search_query: rawQuery,
    subscriber_status: getSubscriberStatus(),
    previous_search_uuid: previous.uuid || null,
    previous_search_query: previous.query || null,
  });
  saveCurrentSearch({ uuid, query: rawQuery });
}

$(document).on('submit', 'form[data-search-track]', (e) => {
  try {
    trackSearchSubmit(e.currentTarget);
  } catch (err) {
    // Never block form submission if tracking fails
  }
});

// Track related-question clicks from the search page and nav dropdown.
$(document).on(
  'click',
  '.semantic-search-paa__link, .menu-search-paa__link',
  (e) => {
    const $link = $(e.currentTarget);

    pushSearchExecutedEvent({
      eventName: 'search_executed_related_question',
      searchTerm: $link.text().trim(),
      searchOrigin: $link.hasClass('menu-search-paa__link') ? 'nav_bar' : 'search_page',
    });
  },
);

// Track top and remaining result clicks.
$(document).on('click', '.semantic-search-card__link, .semantic-search-feed__title a', (e) => {
  handleSearchResultLinkClick(e);
});
